import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import IAppointment from '../../../_interfaces/IAppointment';
import IPatient from '../../../_interfaces/IPatients';
import ISite from '../../../_interfaces/ISite';
import { AppointmentService } from '../../../_services/appointments/appointment.service';
import { CustomersService } from '../../../_services/customers/customers.service';
import { ErrorsService } from '../../../_services/errors/errors.service';
import { PatientsService } from '../../../_services/patients/patients.service';
import { SessionService } from '../../../_services/session/session.service';
import { BaseComponent } from '../../../components/base/base.component';
import { AdminStatePanelComponent } from '../../admin-ui/admin-state-panel/admin-state-panel.component';
import { AdminTableActionsComponent } from '../../admin-ui/admin-table-actions/admin-table-actions.component';
import { AdminListToolbarComponent } from '../../admin-ui/admin-list-toolbar/admin-list-toolbar.component';

@Component({
  standalone: true,
  selector: 'app-table-patients',
  templateUrl: './table-patients.component.html',
  styleUrl: './table-patients.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    TranslatePipe,
    ButtonModule,
    SelectModule,
    AdminListToolbarComponent,
    AdminTableActionsComponent,
    AdminStatePanelComponent,
  ],
})
export class TablePatientsComponent extends BaseComponent implements OnInit {
  public patients: IPatient[] = [];
  public filteredPatients: IPatient[] = [];
  public sites: ISite[] = [];
  public searchValue = '';
  public selectedMainSiteId: number | null = null;
  public tableFirst = 0;
  public tableRows = 20;

  get hasMultipleSites(): boolean {
    return (this.sites?.length || 0) > 1;
  }

  constructor(
    private readonly patientsService: PatientsService,
    private readonly customersService: CustomersService,
    private readonly appointmentsService: AppointmentService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly errorsService: ErrorsService,
    public readonly session: SessionService,
    public readonly translate: TranslateService,
    public readonly router: Router,
    private readonly route: ActivatedRoute,
  ) {
    super(session);
  }

  private normalizeSearchValue(value: unknown): string {
    return `${value || ''}`
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  }

  private readPositiveInteger(value: string | null, fallback: number): number {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
  }

  private hydrateStateFromQueryParams(): void {
    const params = this.route.snapshot.queryParamMap;
    const page = this.readPositiveInteger(params.get('page'), 1);

    this.searchValue = params.get('q') || '';
    this.selectedMainSiteId = this.readPositiveInteger(
      params.get('mainSite'),
      0,
    ) || null;
    this.tableRows = this.readPositiveInteger(params.get('rows'), 20);
    this.tableFirst = (page - 1) * this.tableRows;
  }

  private getCurrentPage(): number {
    return Math.floor(this.tableFirst / this.tableRows) + 1;
  }

  get listQueryParams(): Record<string, string | number> {
    const page = this.getCurrentPage();
    const params: Record<string, string | number> = {};

    if (this.searchValue) params['q'] = this.searchValue;
    if (this.selectedMainSiteId) params['mainSite'] = this.selectedMainSiteId;
    if (page > 1) params['page'] = page;
    if (this.tableRows !== 20) params['rows'] = this.tableRows;

    return params;
  }

  private syncQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        q: this.searchValue || null,
        mainSite: this.selectedMainSiteId || null,
        page: this.getCurrentPage() > 1 ? this.getCurrentPage() : null,
        rows: this.tableRows !== 20 ? this.tableRows : null,
      },
      replaceUrl: true,
    });
  }

  private resetPagination(): void {
    this.tableFirst = 0;
  }

  private clampPaginationToFilteredResults(): void {
    const totalPages = Math.max(
      1,
      Math.ceil(this.filteredPatients.length / this.tableRows),
    );
    const currentPage = Math.min(this.getCurrentPage(), totalPages);
    this.tableFirst = (currentPage - 1) * this.tableRows;
  }

  onSearch(value: string): void {
    this.searchValue = value;
    this.resetPagination();
    this.applyFilters();
    this.syncQueryParams();
  }

  onMainSiteFilterChange(value: number | null): void {
    this.selectedMainSiteId = value ? +value : null;
    this.resetPagination();
    this.syncQueryParams();
    this.load();
  }

  onTablePage(event: { first?: number; rows?: number }): void {
    this.tableFirst = event.first || 0;
    this.tableRows = event.rows || 20;
    this.syncQueryParams();
  }

  private applyFilters(): void {
    const query = this.normalizeSearchValue(this.searchValue);
    const data = [...this.patients];

    if (!query) {
      this.filteredPatients = data;
      this.clampPaginationToFilteredResults();
      return;
    }

    this.filteredPatients = data.filter((patient) =>
      [patient.name, patient.surname, patient.email, patient.phone]
        .filter(Boolean)
        .join(' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .includes(query),
    );
    this.clampPaginationToFilteredResults();
  }

  confirmDelete(patient: IPatient): void {
    if (!patient?.id) {
      return;
    }

    this.appointmentsService.getByPatient(this.customers_id, patient.id).subscribe({
      next: (appointments: IAppointment[]) => {
        const totalAppointments = appointments.length;
        const relatedInfo = totalAppointments
          ? this.translate.instant('ADMIN.PATIENTS.CONFIRM.DELETE.APPOINTMENTS', {
              appointments: totalAppointments,
            })
          : '';

        this.confirmationService.confirm({
          header: this.translate.instant('ADMIN.PATIENTS.CONFIRM.DELETE.TITLE', {
            patient: patient.name,
          }),
          icon: 'pi pi-exclamation-triangle',
          message: `${this.translate.instant('ADMIN.PATIENTS.CONFIRM.DELETE.TEXT', {
            patient: patient.name,
          })} ${relatedInfo}`,
          accept: () => this.delete(patient),
        });
      },
      error: (error) => {
        this.showError(error);
      },
    });
  }

  private delete(patient: IPatient): void {
    if (!patient?.id) {
      return;
    }

    const siteIdForDelete = +(
      patient.main_site_id || this.site?.id || this.sites_id || 0
    );
    if (!siteIdForDelete) {
      this.showError({ status: 403 });
      return;
    }

    this.patientsService
      .delete(this.customers_id, siteIdForDelete, patient)
      .subscribe({
        next: () => {
          this.messageService.add({
            summary: this.translate.instant('MESSAGES.PATIENTS.DELETE.SUMMARY'),
            detail: this.translate.instant('MESSAGES.PATIENTS.DELETE.DETAIL', {
              patient: patient.name,
            }),
            severity: 'success',
          });
          this.load();
        },
        error: (error: HttpErrorResponse) => {
          this.showError(error);
        },
      });
  }

  private loadSites(): void {
    this.customersService.getSites(this.customers_id).subscribe({
      next: (sites: ISite[]) => {
        this.sites = sites || [];
        if (!this.hasMultipleSites) {
          this.selectedMainSiteId = null;
        }
      },
      error: () => {
        this.sites = [];
        this.selectedMainSiteId = null;
      },
    });
  }

  load(): void {
    const query = this.selectedMainSiteId
      ? { main_sites_id: this.selectedMainSiteId }
      : {};

    this.patientsService.getAll(this.customers_id, query).subscribe({
      next: (patients: IPatient[]) => {
        this.patients = patients.map((patient) => ({
          ...patient,
          fullName: `${patient.name || ''} ${patient.surname || ''}`.trim(),
        }));
        this.applyFilters();
        this.syncQueryParams();
      },
      error: (error) => {
        this.showError(error);
      },
    });
  }

  private showError(error: any): void {
    const code = this.errorsService.getMessageFromPatientError(error);
    this.messageService.add({
      detail: this.translate.instant(`MESSAGES.PATIENT.ERROR.${code}`),
      summary: this.translate.instant('MESSAGES.PATIENT.ERROR.SUMMARY'),
      severity: 'error',
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.hydrateStateFromQueryParams();
    this.loadSites();
    this.load();
  }
}
